import { useRoute } from 'react-router5';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listsActions, listsSelector } from '$store/lists';
import { CustomList, DefaultListTypes, ListDataTypes } from '$store/lists/interface';
import { Loader, UsersListItem } from '$shared/components';
import { Header, InlineHeader, Placeholder, ScrollView, SearchInput } from '$uikit';
import './UsersList.sass';
import { IcDelete28, IcSearch24 } from '$assets';
import { useCurrentLang, useTranslator } from '$hooks';
import { _confirm, confirmSheet, format, formatCurrency, transliterate } from '$utils';
import { CurrencyEnum, ListsAdditionalUserInfo, UserModel } from '$store/models';
import { UsersListItemProps } from '$shared/components/UsersListItem/UsersListItem.interface';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import { goBack } from '$navigation/helper';

export const UsersList = () => {
  const {
    route: {
      params: { id },
    },
  } = useRoute();
  const dispatch = useDispatch();
  const t = useTranslator();
  const { listsDataMap, isListLoading } = useSelector(listsSelector);
  const listData: ListDataTypes = listsDataMap[id];
  const [searchValue, setSearchValue] = useState('');
  const currentLang = useCurrentLang();
  const [firstColumnData, setFirstColumnData] = useState<UsersListItemProps['data'][]>(
    [],
  );
  const [secondColumnData, setSecondColumnData] = useState<UsersListItemProps['data'][]>(
    [],
  );

  useEffect(() => {
    dispatch(listsActions.loadListData(id));
  }, [id]);

  const getSum = (amount: number) => {
    return formatCurrency(amount, CurrencyEnum.EUR);
  };

  const formatDate = (timeStamp: number) => {
    return format(new Date(timeStamp * 1000), 'dd MMM yyyy');
  };

  const handleDeleteList = () => {
    confirmSheet(t('list_delete_confirm')).then((ok) => {
      if (!ok) {
        return;
      }

      dispatch(toastActions.setToastLoading());
      Api.post(`/lists/${listData.listId.substring(6)}/delete`)
        .then(() => {
          goBack();
          dispatch(toastActions.setToastSuccess());
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    });
  };

  const getCollapseData = (
    userAdditionalData: ListsAdditionalUserInfo,
    user: UserModel,
  ) => {
    const { total, purchasedTotal, subscription, tipsTotal } = userAdditionalData;
    let subscriptionExpireStatus = '-';

    if (subscription.createdAt && subscription.expiredAt === 0) {
      subscriptionExpireStatus = t('lists_list_collapse_subscription_expired');
    }

    if (subscription.expiredAt > 0) {
      subscriptionExpireStatus = formatDate(subscription.expiredAt);
    }

    return {
      title: t('lists_list_collapse_total_profit'),
      subTitle: getSum(total),
      tabs: [
        {
          tabName: t('lists_list_tab_profit'),
          tabContent: [
            {
              title: t('lists_list_collapse_last_active'),
              subTitle: formatDate(user.lastUpdate),
            },
            {
              title: t('lists_list_collapse_sponsorship'),
              subTitle: getSum(subscription.price),
            },
            {
              title: t('lists_list_collapse_messages'),
              subTitle: getSum(purchasedTotal),
            },
            {
              title: t('lists_list_collapse_tips'),
              subTitle: getSum(tipsTotal),
            },
            {
              title: t('lists_list_collapse_total_profit'),
              subTitle: getSum(total),
              isHighlightedTitle: true,
              isHighlightedLabel: true,
            },
          ],
        },
        {
          tabName: t('lists_list_tab_subscription'),
          tabContent: [
            {
              title: t('lists_list_collapse_price'),
              subTitle: getSum(subscription.price),
              isHighlightedLabel: true,
            },
            {
              title: t('lists_list_collapse_duration'),
              subTitle: subscription.duration
                ? t(`months_count`, { count: subscription.duration })
                : '-',
              isHighlightedLabel: true,
            },
            {
              title: t('lists_list_collapse_started'),
              subTitle: subscription.createdAt ? formatDate(subscription.createdAt) : '-',
              isHighlightedLabel: true,
            },
            {
              title: t('lists_list_collapse_expired'),
              subTitle: subscriptionExpireStatus,
              isHighlightedLabel: true,
            },
          ],
        },
      ],
    };
  };

  const mapListData = () => {
    if (!listData) return null;

    let mappedListData: UsersListItemProps['data'][] = [];

    if (listData.listId.substring(0, 6) === 'custom') {
      const customListData: CustomList = listData as any;
      mappedListData = customListData.users.map((user) => ({
        userData: {
          ...user,
          isFollowFromMe: true,
        },
      }));
    } else if (
      listData.listId === DefaultListTypes.TIPS ||
      listData.listId === DefaultListTypes.PAID_MESSAGES
    ) {
      let key = '';
      let collapseTitle = '';

      if (listData.listId === DefaultListTypes.TIPS) {
        key = 'senders';
        collapseTitle = t('lists_list_tips_profit');
      } else if (listData.listId === DefaultListTypes.PAID_MESSAGES) {
        key = 'buyers';
        collapseTitle = t('lists_list_paid_messages_profit');
      }

      if (!key) {
        return;
      }

      mappedListData = listData[key].map((item) => {
        const user = listData.users[item.userId];
        return {
          userData: {
            ...user,
            isFollowFromMe: user.id in listData.followed,
          },
          contentData: [
            {
              title: collapseTitle,
              subTitle: getSum(item.total),
              isHighlightedTitle: true,
              isHighlightedLabel: true,
            },
          ],
        };
      });
    } else if (listData.listId === DefaultListTypes.FOLLOWINGS) {
      mappedListData = listData.followings.map((user) => ({
        userData: {
          ...user,
          isFollowFromMe: true,
        },
      }));
    } else if (listData.listId === DefaultListTypes.SPONSORS) {
      mappedListData = listData.sponsors.map((data) => {
        const { user } = data;

        return {
          userData: {
            ...user,
            isFollowFromMe: user.id in listData.followed,
          },
          collapseData: getCollapseData(data, user),
        };
      });
    } else if (listData.listId === DefaultListTypes.SUBSCRIBERS) {
      mappedListData = listData.subscribers.map((user) => ({
        userData: {
          ...user,
          isFollowFromMe: listData.followed[user.id],
        },
      }));
    } else if (listData.listId === DefaultListTypes.TELEGRAM) {
      mappedListData = listData.followers.map((follower) => {
        const { user, followed, summary } = follower;

        return {
          userData: {
            ...user,
            isFollowFromMe: followed,
          },
          collapseData: getCollapseData(summary, user),
        };
      });
    } else if (listData.listId === DefaultListTypes.REFERRALS) {
      mappedListData = listData.referrals.map((user) => ({
        userData: {
          ...user,
          isFollowFromMe: true,
        },
      }));
    }

    return mappedListData.filter((item) => {
      const {
        userData: { firstName, lastName, domain },
      } = item;
      const name = `${firstName || ''} ${lastName || ''}`;
      const searchQuery = searchValue.toLowerCase();

      const currentName =
        currentLang.key !== 'ru' ? transliterate(name.toLowerCase()) : name.toLowerCase();

      return (
        (domain && domain.toLowerCase().includes(searchQuery)) ||
        currentName.includes(searchQuery)
      );
    });
  };

  const mappedListData = useMemo(() => {
    return mapListData();
  }, [listData, searchValue]);
  const isNoUsers = !mappedListData || !mappedListData.length;
  const isNoUsersLoaded = isNoUsers && !searchValue;
  const isNoUsersFound = isNoUsers && searchValue;

  const firstColumn = useMemo(() => {
    let items: UsersListItemProps['data'][] = firstColumnData;

    if (items.length === 0) {
      mappedListData?.forEach((item, index) => index % 2 !== 0 && items.push(item));
      setFirstColumnData(items);
    }

    return items;
  }, [mappedListData]);

  const secondColumn = useMemo(() => {
    let items: UsersListItemProps['data'][] = secondColumnData;

    if (items.length === 0) {
      mappedListData?.forEach((item, index) => index % 2 === 0 && items.push(item));
      setSecondColumnData(items);
    }

    return items;
  }, [mappedListData]);

  function renderContent() {
    if (isListLoading && !listData) {
      return <Loader isWrapped />;
    }

    if (!isListLoading && !listData) {
      return null;
    }

    return (
      <ScrollView className="UsersList">
        {!isNoUsersLoaded && (
          <SearchInput
            query={searchValue}
            onChange={(value) => setSearchValue(value)}
            placeholder={t('lists_list_search_placeholder')}
            placeholderIcon={<IcSearch24 />}
          />
        )}
        <InlineHeader>
          {listData.listId.substring(0, 6) === 'custom'
            ? (listData as any).list.title
            : t(`lists_list_name_${id}`)}
        </InlineHeader>
        {!isNoUsersLoaded && !isNoUsersFound ? (
          <div className="UsersList__list_wrap">
            <div>
              {firstColumn.map((item) => (
                <UsersListItem
                  userId={item.userData.id}
                  key={item.userData.id}
                  data={item}
                  isCollapse={true}
                  isShowMoreButton={true}
                  listId={listData.listId}
                />
              ))}
            </div>
            <div>
              {secondColumn.map((item) => (
                <UsersListItem
                  userId={item.userData.id}
                  key={item.userData.id}
                  data={item}
                  isCollapse={true}
                  isShowMoreButton={true}
                  listId={listData.listId}
                />
              ))}
            </div>
          </div>
        ) : listData.listId.substring(0, 6) === 'custom' && !isNoUsersFound ? (
          <Placeholder title={t('custom_list_empty_title')}>
            {t('custom_list_empty_text')}
          </Placeholder>
        ) : (
          <Placeholder>
            {isNoUsersFound
              ? t('no_users_found_placeholder')
              : t('no_users_loaded_placeholder')}
          </Placeholder>
        )}
      </ScrollView>
    );
  }

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          {
            visible: listData && listData.listId.substring(0, 6) === 'custom',
            icon: <IcDelete28 />,
            onClick: () => {
              handleDeleteList();
            },
          },
        ]}
      >
        {t('lists_list_title')}
      </Header>
      {renderContent()}
    </>
  );
};
